import { Team } from '../types';
import { TeamNameDisplayMode } from '../state/useTeamNameDisplayStore';
import { DisplayMode } from './nameHelpers';

/**
 * Get the display name for a team based on the current display mode preference
 * @param team - The team object
 * @param mode - Display mode: 'arabic' (default) or 'french'
 * @returns The appropriate team name based on the display mode
 */
export function getTeamDisplayName(
  team: { name: string; nameAr?: string | null; name_ar?: string | null },
  mode: TeamNameDisplayMode = 'arabic',
): string {
  if (mode === 'arabic') {
    // If Arabic name exists, use it; otherwise fallback to French (for external teams).
    // Accept both camelCase (mapped Team) and snake_case (raw rows / *Lite shapes).
    const ar = team.nameAr ?? team.name_ar;
    return ar && ar.trim() ? ar : team.name;
  } else {
    // French mode: show French name
    return team.name;
  }
}

/**
 * Locale-aware team "full name" (the long descriptive subtitle).
 * There is no Arabic long-name column, so in Arabic mode we fall back to the
 * short Arabic club name (nameAr/name_ar). In French mode we show fullName.
 * Accepts camelCase (mapped Team) or snake_case (raw rows).
 */
export function getLocalizedTeamFullName(
  team: { name: string; nameAr?: string | null; name_ar?: string | null; fullName?: string | null; full_name?: string | null },
  mode: TeamNameDisplayMode = 'arabic',
): string {
  if (mode === 'arabic') {
    const ar = team.nameAr ?? team.name_ar;
    if (ar && ar.trim()) return ar;
  }
  const full = team.fullName ?? team.full_name;
  return (full && full.trim()) ? full : team.name;
}

/**
 * Get both names formatted for display (useful for dropdowns/selectors)
 * @param team - The team object
 * @param mode - Display mode: determines which name is primary
 * @returns Formatted string with both names
 */
export function getTeamBothNames(team: Team, mode: TeamNameDisplayMode = 'arabic'): string {
  const hasArabic = team.nameAr && team.nameAr.trim();

  if (!hasArabic) {
    // If no Arabic name, just show French name
    return team.name;
  }

  if (mode === 'arabic') {
    // Arabic primary: "الأهلي (ES Sétif)"
    return `${team.nameAr} (${team.name})`;
  } else {
    // French primary: "ES Sétif (الأهلي)"
    return `${team.name} (${team.nameAr})`;
  }
}

/**
 * Check if team has Arabic name
 * @param team - The team object
 * @returns True if team has an Arabic name
 */
export function teamHasArabicName(team: Team): boolean {
  return !!(team.nameAr && team.nameAr.trim());
}

/**
 * Locale-aware stadium name: returns nameAr when mode='arabic' (with French fallback),
 * or always the French name when mode='french'.
 */
export function getLocalizedStadiumName(
  stadium: { name: string; nameAr?: string | null; name_ar?: string | null },
  mode: DisplayMode
): string {
  if (mode === 'arabic') {
    const ar = stadium.nameAr ?? stadium.name_ar;
    return (ar && ar.trim()) ? ar : stadium.name;
  }
  return stadium.name;
}

/**
 * Locale-aware league name. Accepts either snake_case (`name_ar`, from raw rows/embeds)
 * or camelCase (`nameAr`) Arabic fields. Falls back to the French name when the Arabic
 * one is missing or when mode='french'.
 */
export function getLocalizedLeagueName(
  league: { name: string; name_ar?: string | null; nameAr?: string | null },
  mode: DisplayMode
): string {
  if (mode === 'arabic') {
    const ar = league.name_ar ?? league.nameAr;
    return (ar && ar.trim()) ? ar : league.name;
  }
  return league.name;
}

/**
 * Locale-aware league-group name. Same shape/fallback rules as getLocalizedLeagueName.
 */
export function getLocalizedGroupName(
  group: { name: string; name_ar?: string | null; nameAr?: string | null },
  mode: DisplayMode
): string {
  if (mode === 'arabic') {
    const ar = group.name_ar ?? group.nameAr;
    return (ar && ar.trim()) ? ar : group.name;
  }
  return group.name;
}
